import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, AlertCircle, Loader2, Eye } from 'lucide-react';
import { orderService } from '../services/orderService';
import { toast } from 'react-hot-toast';
import config from '../config';

export const OrderTracker = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await orderService.getById(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('خطا در دریافت اطلاعات سفارش');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!window.confirm('آیا از تحویل گرفتن این سفارش اطمینان دارید؟')) return;
    
    setConfirming(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${orderId}/confirm/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('سفارش با موفقیت تحویل گرفته شد');
        fetchOrder();
      } else {
        toast.error('خطا در تایید تحویل');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setConfirming(false);
    }
  };

  const handleSubmitReview = async (productId, rating, comment) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/reviews/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product: productId,
          rating,
          comment,
          order: orderId
        })
      });
      
      if (response.ok) {
        toast.success('نظر شما با موفقیت ثبت شد');
        fetchOrder();
      } else {
        toast.error('خطا در ثبت نظر');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-8 h-8 text-yellow-500" />;
      case 'paid': return <Package className="w-8 h-8 text-blue-500" />;
      case 'processing': return <Package className="w-8 h-8 text-purple-500" />;
      case 'shipped': return <Truck className="w-8 h-8 text-indigo-500" />;
      case 'delivered': return <CheckCircle className="w-8 h-8 text-green-500" />;
      default: return <AlertCircle className="w-8 h-8 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    const map = {
      pending: 'در انتظار پرداخت',
      paid: 'پرداخت شده',
      processing: 'در حال پردازش',
      shipped: 'ارسال شده',
      delivered: 'تحویل شده',
      cancelled: 'لغو شده'
    };
    return map[status] || status;
  };

  const timelineSteps = [
    { status: 'pending', label: 'ثبت سفارش' },
    { status: 'paid', label: 'پرداخت' },
    { status: 'processing', label: 'پردازش' },
    { status: 'shipped', label: 'ارسال' },
    { status: 'delivered', label: 'تحویل' }
  ];

  const currentStepIndex = timelineSteps.findIndex(s => s.status === order?.status);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">سفارش یافت نشد</h2>
          <Link to="/profile" className="mt-4 inline-block text-blue-600">بازگشت به پروفایل</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Link to="/profile" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
            ← بازگشت به پروفایل
          </Link>
        </div>

        {/* Order Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold">سفارش #{order.id}</h1>
              <p className="text-gray-500 mt-1">
                تاریخ ثبت: {new Date(order.created_at).toLocaleDateString('fa-IR')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(order.status)}
              <span className="font-bold text-lg">{getStatusText(order.status)}</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold mb-6">وضعیت سفارش</h2>
          <div className="relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0"></div>
            <div className="relative z-10 flex justify-between">
              {timelineSteps.map((step, idx) => (
                <div key={step.status} className="text-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                    idx <= currentStepIndex ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {idx < currentStepIndex ? <CheckCircle className="w-5 h-5" /> : idx === currentStepIndex ? getStatusIcon(order.status) : idx + 1}
                  </div>
                  <p className={`text-xs ${idx <= currentStepIndex ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                    {step.label}
                  </p>
                  {idx === currentStepIndex && order.status === 'shipped' && order.tracking_code && (
                    <p className="text-xs text-blue-600 mt-1">کد رهگیری: {order.tracking_code}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">محصولات سفارش</h2>
          <div className="divide-y divide-gray-200">
            {order.items?.map((item) => (
              <div key={item.id} className="py-4 flex gap-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={item.product?.main_image || '/placeholder.jpg'} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{item.product?.name}</h3>
                  <p className="text-gray-500 text-sm">تعداد: {item.quantity}</p>
                  <p className="text-blue-600 font-bold mt-1">{item.price?.toLocaleString()} تومان</p>
                </div>
                
                {/* Review Button - only for delivered orders */}
                {order.status === 'delivered' && !item.has_review && (
                  <button
                    onClick={() => {
                      const rating = prompt('امتیاز خود را وارد کنید (1 تا 5):');
                      if (rating && rating >= 1 && rating <= 5) {
                        const comment = prompt('نظر خود را بنویسید:');
                        handleSubmitReview(item.product.id, parseInt(rating), comment);
                      }
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    ثبت نظر
                  </button>
                )}
                {item.has_review && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    نظر ثبت شده
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Confirmation */}
        {order.status === 'shipped' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold mb-4">تایید تحویل</h2>
            <p className="text-gray-600 mb-4">
              در صورت دریافت سفارش، لطفاً تحویل خود را تایید کنید.
            </p>
            <button
              onClick={handleConfirmDelivery}
              disabled={confirming}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              تایید تحویل سفارش
            </button>
          </div>
        )}

        {/* Shipping Address */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <h2 className="text-lg font-bold mb-4">آدرس تحویل</h2>
          <p className="text-gray-600">{order.shipping_address || 'آدرسی ثبت نشده است'}</p>
        </div>
      </div>
    </div>
  );
};
